/**
 * SharePointCredentialsStep — Step 1 of the project's SharePoint setup wizard.
 *
 * Renders the tenant-credentials status pill (green when Azure AD app is
 * configured for this company; amber when missing). Read-only; admins follow
 * the link to /settings to manage credentials at company scope.
 */
import { Link } from "react-router-dom";
import { AlertTriangle } from "lucide-react";
import { useI18n } from "../../context/I18nContext";

export default function SharePointCredentialsStep({ companyCreds }) {
  const { t } = useI18n();
  const configured = !!companyCreds?.configured;
  return (
    <div className="bg-white border border-zinc-200 rounded-md p-5">
      <div className="flex items-center gap-2 pb-3 border-b border-zinc-100">
        <span className="cs-label">step 1</span>
        <h4 className="font-heading text-sm font-semibold">{t("step_credentials_title")}</h4>
      </div>
      {configured ? (
        <div className="mt-4 flex items-center justify-between" data-testid="sp-creds-status">
          <div className="flex items-center gap-3">
            <span className="w-2 h-2 rounded-full bg-cs-success" />
            <div>
              <div className="text-sm font-medium text-zinc-900">{companyCreds.display_name || "Azure AD"}</div>
              <div className="font-mono text-[11px] text-zinc-500">tenant: {companyCreds.tenant_id}</div>
            </div>
          </div>
          <Link to="/settings" className="text-xs font-semibold text-cs-accent hover:underline" data-testid="sp-creds-manage-link">
            {t("edit_credentials")} →
          </Link>
        </div>
      ) : (
        <div className="mt-4 flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-md p-4" data-testid="sp-creds-missing">
          <AlertTriangle size={18} className="text-amber-700 shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="text-sm font-semibold text-amber-900">{t("not_configured")}</div>
            <p className="text-xs text-amber-800 mt-1">{t("sp_creds_missing_help")}</p>
            <Link to="/settings" className="inline-block mt-3 text-xs font-semibold text-amber-900 underline" data-testid="sp-creds-cta-link">
              {t("go_to_settings")} →
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
